/* BG: Bio Gold desktop header behaviour (sections/bg-header.liquid). Spec: _specs/01-header-desktop.md §4, §8.4.
   - <bg-sticky-header>: Dawn StickyHeader semantics (on-scroll-up), hides with a wrapper transform, toggles
     shopify-section-header-show on every reveal (drives the CSS fadeInDown, A15); dropdown touch/Escape helper;
     search open/close side effects (body[data-bg-drawer="header-search"], section raised + scrollbar padding).
   - <bg-lang-sidebar>: 370px language drawer (body[data-bg-drawer="localization"], focus trap, Escape, scrim).
   - Mirrors the #cart-icon-bubble count into the mobile header's [data-bg-cart-count] (A3).
   Relies on Dawn's global.js (trapFocus/removeTrapFocus), details-modal.js and predictive-search.js. */
(() => {
  const desktop = window.matchMedia('(min-width: 1025px)');
  const STICKY_CLASSES = ['shopify-section-header-hidden', 'shopify-section-header-sticky', 'shopify-section-header-show', 'animate'];

  const setDrawer = (value) => {
    document.body.dataset.bgDrawer = value;
  };

  const clearDrawer = (value) => {
    if (document.body.dataset.bgDrawer === value) delete document.body.dataset.bgDrawer;
  };

  const scrollbarWidth = () => Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  class BgStickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.closest('.shopify-section');
      if (!this.header) return;

      this.stickyType = this.dataset.stickyType || 'on-scroll-up';
      this.alwaysSticky = this.stickyType === 'always';
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearches = this.querySelectorAll('predictive-search');

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

      this.measure();
      this.setHeaderHeight();
      this.initMenu();
      this.initSearch();

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('resize', this.onResize, { passive: true });
      desktop.addEventListener('change', this.onResize);

      if (this.stickyType === 'none') return;
      if (this.alwaysSticky) this.header.classList.add('shopify-section-header-sticky');
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      desktop.removeEventListener('change', this.onResize);
      document.removeEventListener('click', this.onDocumentClick);
    }

    /* Page coordinates of the section at rest (Dawn uses a one-shot IntersectionObserver; a rect is also correct when
       the page loads already scrolled). On the home page the section is 0px tall, so any scroll past it flips the
       transparent bar to solid, as live. */
    measure() {
      if (STICKY_CLASSES.some((cls) => this.header.classList.contains(cls)) && !this.alwaysSticky) return;
      const rect = this.header.getBoundingClientRect();
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      this.headerBounds = { top: rect.top + scrollY, bottom: rect.bottom + scrollY };
    }

    /* --header-height from the bar (125px), not the section (0px on the home page); desktop only so the mobile
       header keeps ownership below 1025px */
    setHeaderHeight() {
      if (!desktop.matches) return;
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    }

    onResize() {
      if (!desktop.matches && !this.alwaysSticky) {
        this.header.classList.remove('scrolled-past-header', ...STICKY_CLASSES);
      }
      this.measure();
      this.setHeaderHeight();
      this.header.style.setProperty('--bg-scrollbar-width', `${scrollbarWidth()}px`);
    }

    onScroll() {
      if (!desktop.matches) return;
      if (this.header.classList.contains('bg-header--raised')) return;
      if ([...this.predictiveSearches].some((search) => search.isOpen)) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);
          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.alwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.header.classList.remove('shopify-section-header-show');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.alwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'shopify-section-header-show', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.alwaysSticky) return;
      this.header.classList.remove(...STICKY_CLASSES);
    }

    closeMenuDisclosure() {
      this.querySelectorAll('.bg-header__item--has-children').forEach((item) => item.classList.remove('is-open'));
    }

    closeSearchModal() {
      this.querySelectorAll('details-modal').forEach((modal) => {
        if (typeof modal.isOpen === 'function' && modal.isOpen()) modal.close(false);
      });
    }

    /* Dropdown helper (A8/A9): hover and :focus-within open it in CSS. Touch: the first tap on a parent link opens
       the panel, the second follows the link (a deliberate choice; live leaves the first tap to the browser).
       Escape closes and returns focus to the parent link; tapping elsewhere closes. */
    initMenu() {
      this.menuItems = this.querySelectorAll('.bg-header__item--has-children');
      if (!this.menuItems.length) return;

      this.menuItems.forEach((item) => {
        const link = item.querySelector(':scope > .bg-header__link');
        if (!link) return;

        link.addEventListener('pointerdown', (event) => {
          this.touchTarget = event.pointerType === 'touch' && !item.classList.contains('is-open') ? item : null;
        });

        link.addEventListener('click', (event) => {
          if (this.touchTarget !== item) return;
          event.preventDefault();
          this.touchTarget = null;
          this.closeMenuDisclosure();
          item.classList.remove('is-closed');
          item.classList.add('is-open');
        });

        item.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          item.classList.remove('is-open');
          item.classList.add('is-closed');
          link.focus();
        });

        item.addEventListener('mouseleave', () => item.classList.remove('is-closed'));
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) item.classList.remove('is-closed', 'is-open');
        });
      });

      document.addEventListener('click', this.onDocumentClick);
    }

    onDocumentClick(event) {
      this.menuItems.forEach((item) => {
        if (!item.contains(event.target)) item.classList.remove('is-open');
      });
    }

    /* Search: Dawn's details-modal owns open/close, Escape, focus trap and outside-click. We add the live side effects:
       body drawer state (scroll lock + scrollbar padding from 00-global), section raised to z 101 and its own
       overflow:hidden + scrollbar padding (measured before the scrollbar disappears). */
    initSearch() {
      const modal = this.querySelector('details-modal');
      const details = modal?.querySelector('details');
      if (!details) return;

      modal.addEventListener(
        'click',
        (event) => {
          if (!details.open && event.target.closest('summary')) {
            this.header.style.setProperty('--bg-scrollbar-width', `${scrollbarWidth()}px`);
          }
        },
        true
      );

      details.addEventListener('toggle', () => {
        const open = details.open;
        this.header.classList.toggle('bg-header--search-open', open);
        if (open) {
          this.header.classList.add('bg-header--raised');
          setDrawer('header-search');
        } else {
          if (!this.header.classList.contains('bg-header--lang-open')) this.header.classList.remove('bg-header--raised');
          clearDrawer('header-search');
        }
      });
    }
  }

  class BgLangSidebar extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      if (!this.section) return;

      this.opener = this.section.querySelector('[data-bg-lang-open]');
      this.closeButton = this.querySelector('[data-bg-lang-close]');
      this.scrim = this.section.querySelector('[data-bg-lang-scrim]');
      this.form = this.querySelector('form');

      this.opener?.addEventListener('click', () => (this.hasAttribute('open') ? this.close() : this.open()));
      this.closeButton?.addEventListener('click', () => this.close());
      this.scrim?.addEventListener('click', () => this.close());
      this.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      });

      this.querySelectorAll('[data-bg-lang-value]').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          if (!this.form) return;
          const input = this.form.querySelector('input[name="locale_code"]');
          if (input) input.value = link.dataset.bgLangValue;
          this.form.submit();
        });
      });
    }

    open() {
      window.clearTimeout(this.lowerTimer);
      this.section.classList.add('bg-header--raised', 'bg-header--lang-open');
      this.setAttribute('open', '');
      this.opener?.setAttribute('aria-expanded', 'true');
      setDrawer('localization');
      if (typeof trapFocus === 'function') trapFocus(this, this.closeButton || this);
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.section.classList.remove('bg-header--lang-open');
      this.opener?.setAttribute('aria-expanded', 'false');
      clearDrawer('localization');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
      // keep the section above the page until the 500ms slide-out ends
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.lowerTimer = setTimeout(
        () => {
          if (!this.section.classList.contains('bg-header--search-open') && !this.hasAttribute('open')) {
            this.section.classList.remove('bg-header--raised');
          }
        },
        reduced ? 0 : 500
      );
    }
  }

  if (!customElements.get('bg-sticky-header')) customElements.define('bg-sticky-header', BgStickyHeader);
  if (!customElements.get('bg-lang-sidebar')) customElements.define('bg-lang-sidebar', BgLangSidebar);

  /* A3: the desktop link owns #cart-icon-bubble (Dawn re-renders it after cart changes); copy its count into the
     other header bubbles (the mobile header's [data-bg-cart-count]; bg-header-mobile.js also updates it on cartUpdate). */
  const bubble = document.getElementById('cart-icon-bubble');
  if (bubble && !bubble.dataset.bgCountMirror) {
    bubble.dataset.bgCountMirror = 'true';
    const sync = () => {
      const count = bubble.querySelector('.bg-header__bubble-count');
      if (!count) return;
      const value = count.textContent.trim();
      document.querySelectorAll('[data-bg-cart-count]').forEach((el) => {
        if (el.textContent !== value) el.textContent = value;
      });
    };
    new MutationObserver(sync).observe(bubble, { childList: true, subtree: true, characterData: true });
  }
})();
